export const IMAGE_CROP_ORIGINAL = 'original'
export const IMAGE_CROP_FREEFORM = 'freeform'

export const MIN_IMAGE_CROP_PERCENT = 8

export const IMAGE_CROP_RATIO_OPTIONS = [
  { label: 'Original', value: IMAGE_CROP_ORIGINAL, ratio: null, cssRatio: null },
  { label: 'Free', value: IMAGE_CROP_FREEFORM, ratio: null, cssRatio: null },
  { label: 'Square', value: '1:1', ratio: 1, cssRatio: '1 / 1' },
  { label: '4:3', value: '4:3', ratio: 4 / 3, cssRatio: '4 / 3' },
  { label: '16:9', value: '16:9', ratio: 16 / 9, cssRatio: '16 / 9' },
  { label: '3:4', value: '3:4', ratio: 3 / 4, cssRatio: '3 / 4' },
  { label: '4:5', value: '4:5', ratio: 4 / 5, cssRatio: '4 / 5' },
]

const CROP_STYLE_PROPS = new Set([
  'aspect-ratio',
  'display',
  'height',
  'left',
  'max-width',
  'object-fit',
  'object-position',
  'position',
  'top',
  'width',
])

const CROP_CONTAINER_STYLE_PROPS = new Set([
  'aspect-ratio',
  'overflow',
  'position',
])

const DATA_CROP_ATTRS = {
  ratio: 'dataCropRatio',
  x: 'dataCropX',
  y: 'dataCropY',
  left: 'dataCropLeft',
  top: 'dataCropTop',
  width: 'dataCropWidth',
  height: 'dataCropHeight',
  aspect: 'dataCropAspect',
}

export function clampCropPercent(value, fallback = 50) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return fallback
  return Math.min(100, Math.max(0, Math.round(numeric)))
}

export function clampCropValue(value, fallback = 0, min = 0, max = 100) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return fallback
  return Math.min(max, Math.max(min, Math.round(numeric * 100) / 100))
}

export function getCropRatioOption(value) {
  return IMAGE_CROP_RATIO_OPTIONS.find(option => option.value === value) || IMAGE_CROP_RATIO_OPTIONS[0]
}

export function parseCssStyle(style) {
  if (!style || typeof style !== 'string') return {}

  return style.split(';').reduce((styles, declaration) => {
    const separatorIndex = declaration.indexOf(':')
    if (separatorIndex === -1) return styles

    const property = declaration.slice(0, separatorIndex).trim().toLowerCase()
    const value = declaration.slice(separatorIndex + 1).trim()
    if (property && value) styles[property] = value
    return styles
  }, {})
}

export function stringifyCssStyle(styles) {
  return Object.entries(styles)
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
    .map(([property, value]) => `${property}: ${String(value).trim()}`)
    .join('; ')
}

function parsePixelValue(value) {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'number' && Number.isFinite(value)) return value

  const match = String(value).match(/^([0-9.]+)\s*(?:px)?$/i)
  if (!match) return null

  const parsed = Number(match[1])
  return Number.isFinite(parsed) ? parsed : null
}

function getPixelWidthFromStyle(style) {
  const styles = parseCssStyle(style)
  return parsePixelValue(styles.width)
}

export function getImageCropWidth(attrs = {}, renderedWidth = null, fallbackWidth = 640) {
  const candidates = [
    renderedWidth,
    attrs.width,
    getPixelWidthFromStyle(attrs.style),
    getPixelWidthFromStyle(attrs.containerStyle),
  ]

  for (const candidate of candidates) {
    const parsed = parsePixelValue(candidate)
    if (parsed && parsed > 0) return Math.round(parsed)
  }

  return fallbackWidth
}

function parseCropRatioFromStyle(style) {
  const styles = parseCssStyle(style)
  const ratioStyle = styles['aspect-ratio']
  if (!ratioStyle) return { ratio: IMAGE_CROP_ORIGINAL, aspect: null }

  const compact = ratioStyle.replace(/\s+/g, '')
  const match = IMAGE_CROP_RATIO_OPTIONS.find(option => {
    if (!option.cssRatio) return false
    return option.cssRatio.replace(/\s+/g, '') === compact
  })

  if (match) return { ratio: match.value, aspect: match.ratio }

  const slashMatch = compact.match(/^([0-9.]+)\/([0-9.]+)$/)
  if (slashMatch) {
    const width = Number(slashMatch[1])
    const height = Number(slashMatch[2])
    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      return { ratio: IMAGE_CROP_FREEFORM, aspect: width / height }
    }
  }

  const numeric = Number(compact)
  if (Number.isFinite(numeric) && numeric > 0) {
    return { ratio: IMAGE_CROP_FREEFORM, aspect: numeric }
  }

  return { ratio: IMAGE_CROP_ORIGINAL, aspect: null }
}

function parseObjectPosition(style) {
  const styles = parseCssStyle(style)
  const value = styles['object-position']
  if (!value) return { x: 50, y: 50 }

  const matches = [...value.matchAll(/([0-9.]+)%/g)]
  if (matches.length < 2) return { x: 50, y: 50 }

  return {
    x: clampCropPercent(matches[0][1]),
    y: clampCropPercent(matches[1][1]),
  }
}

export function normalizeCropRect(rect = {}) {
  const width = clampCropValue(rect.width, 100, MIN_IMAGE_CROP_PERCENT, 100)
  const height = clampCropValue(rect.height, 100, MIN_IMAGE_CROP_PERCENT, 100)
  return {
    left: clampCropValue(rect.left, 0, 0, 100 - width),
    top: clampCropValue(rect.top, 0, 0, 100 - height),
    width,
    height,
  }
}

export function isFullCropRect(rect = {}) {
  const normalized = normalizeCropRect(rect)
  return normalized.left === 0 &&
    normalized.top === 0 &&
    normalized.width === 100 &&
    normalized.height === 100
}

export function getCropRectFromState(cropState = {}) {
  return normalizeCropRect({
    left: cropState.cropLeft,
    top: cropState.cropTop,
    width: cropState.cropWidth,
    height: cropState.cropHeight,
  })
}

export function getImageCropRectAspect(rect = {}, imageAspect = 1) {
  const normalized = normalizeCropRect(rect)
  const aspect = Number(imageAspect)
  const safeImageAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1
  return Math.round((safeImageAspect * normalized.width / normalized.height) * 10000) / 10000
}

export function buildCenteredCropRectForAspect(targetAspect, imageAspect = 1) {
  const target = Number(targetAspect)
  const image = Number(imageAspect)
  if (!Number.isFinite(target) || target <= 0 || !Number.isFinite(image) || image <= 0) {
    return normalizeCropRect()
  }

  const percentRatio = target / image
  if (percentRatio >= 1) {
    const height = 100 / percentRatio
    return normalizeCropRect({
      left: 0,
      top: (100 - height) / 2,
      width: 100,
      height,
    })
  }

  const width = 100 * percentRatio
  return normalizeCropRect({
    left: (100 - width) / 2,
    top: 0,
    width,
    height: 100,
  })
}

function hasStoredCropRect(attrs = {}) {
  return attrs.dataCropLeft !== null &&
    attrs.dataCropLeft !== undefined &&
    attrs.dataCropTop !== null &&
    attrs.dataCropTop !== undefined &&
    attrs.dataCropWidth !== null &&
    attrs.dataCropWidth !== undefined &&
    attrs.dataCropHeight !== null &&
    attrs.dataCropHeight !== undefined
}

function getCropRectFromAttrs(attrs = {}) {
  if (!hasStoredCropRect(attrs)) return normalizeCropRect()
  return normalizeCropRect({
    left: attrs.dataCropLeft,
    top: attrs.dataCropTop,
    width: attrs.dataCropWidth,
    height: attrs.dataCropHeight,
  })
}

function parseCropAspect(value, fallback = null) {
  const numeric = Number(value)
  return Number.isFinite(numeric) && numeric > 0
    ? Math.round(numeric * 10000) / 10000
    : fallback
}

export function getImageCropStateFromAttrs(attrs = {}, renderedWidth = null) {
  const styleCrop = parseCropRatioFromStyle(attrs.style)
  const ratio = attrs.dataCropRatio || styleCrop.ratio
  const position = parseObjectPosition(attrs.style)
  const hasRect = hasStoredCropRect(attrs)
  const rect = getCropRectFromAttrs(attrs)
  const ratioOption = getCropRatioOption(ratio)
  const aspect = parseCropAspect(attrs.dataCropAspect, styleCrop.aspect || ratioOption.ratio)

  return {
    ratio: ratioOption.value,
    x: clampCropPercent(attrs.dataCropX ?? position.x),
    y: clampCropPercent(attrs.dataCropY ?? position.y),
    width: getImageCropWidth(attrs, renderedWidth),
    cropLeft: rect.left,
    cropTop: rect.top,
    cropWidth: rect.width,
    cropHeight: rect.height,
    cropAspect: aspect,
    hasCropRect: hasRect,
  }
}

function setCssProperty(style, property, value) {
  const styles = parseCssStyle(style)
  if (value === null || value === undefined || value === '') {
    delete styles[property]
  } else {
    styles[property] = value
  }
  return stringifyCssStyle(styles)
}

function removeCropStyles(style) {
  const styles = parseCssStyle(style)
  for (const property of CROP_STYLE_PROPS) {
    delete styles[property]
  }
  return stringifyCssStyle(styles)
}

function removeCropContainerStyles(style) {
  const styles = parseCssStyle(style)
  for (const property of CROP_CONTAINER_STYLE_PROPS) {
    delete styles[property]
  }
  return stringifyCssStyle(styles)
}

function hasCropRectState(cropState = {}) {
  if (cropState.hasCropRect === false) return false
  return cropState.cropLeft !== undefined ||
    cropState.cropTop !== undefined ||
    cropState.cropWidth !== undefined ||
    cropState.cropHeight !== undefined
}

export function buildImageCropAttrs(attrs = {}, cropState = {}) {
  const ratioOption = getCropRatioOption(cropState.ratio)
  const width = getImageCropWidth(attrs, cropState.width)
  const nextAttrs = {
    ...attrs,
    width,
  }

  nextAttrs.containerStyle = setCssProperty(attrs.containerStyle, 'width', `${width}px`)

  if (ratioOption.value === IMAGE_CROP_ORIGINAL) {
    nextAttrs.style = removeCropStyles(attrs.style)
    nextAttrs.containerStyle = removeCropContainerStyles(nextAttrs.containerStyle)
    nextAttrs[DATA_CROP_ATTRS.ratio] = null
    nextAttrs[DATA_CROP_ATTRS.x] = null
    nextAttrs[DATA_CROP_ATTRS.y] = null
    nextAttrs[DATA_CROP_ATTRS.left] = null
    nextAttrs[DATA_CROP_ATTRS.top] = null
    nextAttrs[DATA_CROP_ATTRS.width] = null
    nextAttrs[DATA_CROP_ATTRS.height] = null
    nextAttrs[DATA_CROP_ATTRS.aspect] = null
    return nextAttrs
  }

  if (hasCropRectState(cropState)) {
    const rect = getCropRectFromState(cropState)
    if (ratioOption.value === IMAGE_CROP_FREEFORM && isFullCropRect(rect)) {
      return buildImageCropAttrs(attrs, {
        ...cropState,
        ratio: IMAGE_CROP_ORIGINAL,
        hasCropRect: false,
      })
    }

    const aspect = parseCropAspect(cropState.cropAspect, ratioOption.ratio || getImageCropRectAspect(rect))
    const centerX = clampCropPercent(rect.left + rect.width / 2)
    const centerY = clampCropPercent(rect.top + rect.height / 2)
    const styles = parseCssStyle(removeCropStyles(attrs.style))

    styles.display = 'block'
    styles.position = 'relative'
    styles.width = `${Math.round((10000 / rect.width) * 100) / 100}%`
    styles.height = 'auto'
    styles['max-width'] = 'none'
    styles.left = `${Math.round((-rect.left / rect.width * 100) * 100) / 100}%`
    styles.top = `${Math.round((-rect.top / rect.height * 100) * 100) / 100}%`

    let containerStyle = setCssProperty(attrs.containerStyle, 'width', `${width}px`)
    containerStyle = setCssProperty(containerStyle, 'aspect-ratio', `${aspect} / 1`)
    containerStyle = setCssProperty(containerStyle, 'overflow', 'hidden')
    containerStyle = setCssProperty(containerStyle, 'position', 'relative')

    nextAttrs.style = stringifyCssStyle(styles)
    nextAttrs.containerStyle = containerStyle
    nextAttrs[DATA_CROP_ATTRS.ratio] = ratioOption.value
    nextAttrs[DATA_CROP_ATTRS.x] = centerX
    nextAttrs[DATA_CROP_ATTRS.y] = centerY
    nextAttrs[DATA_CROP_ATTRS.left] = rect.left
    nextAttrs[DATA_CROP_ATTRS.top] = rect.top
    nextAttrs[DATA_CROP_ATTRS.width] = rect.width
    nextAttrs[DATA_CROP_ATTRS.height] = rect.height
    nextAttrs[DATA_CROP_ATTRS.aspect] = aspect

    return nextAttrs
  }

  const x = clampCropPercent(cropState.x)
  const y = clampCropPercent(cropState.y)
  const styles = parseCssStyle(attrs.style)
  styles.height = 'auto'
  styles['aspect-ratio'] = ratioOption.cssRatio
  styles['object-fit'] = 'cover'
  styles['object-position'] = `${x}% ${y}%`

  nextAttrs.style = stringifyCssStyle(styles)
  nextAttrs[DATA_CROP_ATTRS.ratio] = ratioOption.value
  nextAttrs[DATA_CROP_ATTRS.x] = x
  nextAttrs[DATA_CROP_ATTRS.y] = y
  nextAttrs[DATA_CROP_ATTRS.left] = null
  nextAttrs[DATA_CROP_ATTRS.top] = null
  nextAttrs[DATA_CROP_ATTRS.width] = null
  nextAttrs[DATA_CROP_ATTRS.height] = null
  nextAttrs[DATA_CROP_ATTRS.aspect] = ratioOption.ratio

  return nextAttrs
}

export function isImageCropActive(attrs = {}) {
  const state = getImageCropStateFromAttrs(attrs)
  return state.ratio !== IMAGE_CROP_ORIGINAL || !isFullCropRect({
    left: state.cropLeft,
    top: state.cropTop,
    width: state.cropWidth,
    height: state.cropHeight,
  })
}
